import { useEffect } from "react";
import { GraduationCap, Plus, X, Trash2 } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const PRESETS = ["B.Tech", "B.E.", "B.Sc", "B.Com", "BBA", "BA", "B.Arch", "BCA", "MBBS", "LLB",
  "M.Tech", "M.Sc", "MBA", "MA", "MCA", "M.Com", "PhD", "Other"];

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 text-sm focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors";
const labelCls = "text-xs font-medium text-slate-600";

const blank = () => ({ id: uid(), degree: "B.Tech", other: "", status: "pursuing", current_semester: "",
  completion_year: "", overall_cgpa: "", institution: "", semesters: [] });

export default function EducationSection({ degrees, setDegrees }) {
  // Backfill stable ids (degrees loaded from DB or older blanks lack them) so React keys are reliable.
  useEffect(() => {
    if (degrees.some((d) => !d.id || (d.semesters || []).some((s) => !s.id))) {
      setDegrees(degrees.map((d) => ({
        ...d, id: d.id || uid(),
        semesters: (d.semesters || []).map((s) => ({ ...s, id: s.id || uid() })),
      })));
    }
  }, [degrees, setDegrees]);

  const update = (i, patch) => setDegrees(degrees.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const addDegree = () => setDegrees([...degrees, blank()]);
  const removeDegree = (i) => setDegrees(degrees.filter((_, idx) => idx !== i));

  const addSem = (i) => {
    const d = degrees[i];
    const n = d.semesters.length + 1;
    update(i, { semesters: [...d.semesters, { id: uid(), sem: n, grade: "" }] });
  };
  const setSem = (i, j, grade) => update(i, { semesters: degrees[i].semesters.map((s, k) => (k === j ? { ...s, grade } : s)) });
  const removeSem = (i, j) => update(i, { semesters: degrees[i].semesters.filter((_, k) => k !== j) });

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap size={16} className="text-[#6366F1]" />
        <h3 className="font-bold text-slate-900 text-sm">Higher education</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">Add each degree — currently pursuing or completed. Only the degree and status are required.</p>

      <div className="space-y-4">
        {degrees.map((d, i) => (
          <div key={d.id || i} data-testid={`degree-block-${i}`} className="rounded-xl border border-slate-200 p-4 bg-slate-50/60">
            <div className="flex items-start justify-between gap-3">
              <div className="grid sm:grid-cols-2 gap-3 flex-1">
                <div>
                  <label className={labelCls}>Degree</label>
                  <select data-testid={`degree-select-${i}`} value={d.degree} onChange={(e) => update(i, { degree: e.target.value })} className={`${inputCls} mt-1 bg-white`}>
                    {PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                {d.degree === "Other" ? (
                  <div>
                    <label className={labelCls}>Degree name</label>
                    <input data-testid={`degree-other-${i}`} value={d.other} onChange={(e) => update(i, { other: e.target.value })} placeholder="e.g. B.Des" className={`${inputCls} mt-1`} />
                  </div>
                ) : (
                  <div>
                    <label className={labelCls}>Institution (optional)</label>
                    <input data-testid={`degree-inst-${i}`} value={d.institution} onChange={(e) => update(i, { institution: e.target.value })} placeholder="e.g. IIT Bombay" className={`${inputCls} mt-1`} />
                  </div>
                )}
              </div>
              {degrees.length > 1 && (
                <button data-testid={`degree-remove-${i}`} onClick={() => removeDegree(i)} className="text-slate-300 hover:text-red-500 transition-colors mt-5"><Trash2 size={16} /></button>
              )}
            </div>

            {/* status toggle */}
            <div className="flex gap-2 mt-3">
              {[{ v: "pursuing", l: "Currently pursuing" }, { v: "completed", l: "Completed" }].map((s) => (
                <button key={s.v} type="button" data-testid={`degree-status-${i}-${s.v}`} onClick={() => update(i, { status: s.v })}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                    d.status === s.v ? "border-[#6366F1] bg-[#6366F1]/5 text-[#4F46E5]" : "border-slate-200 text-slate-500 hover:border-slate-300 bg-white"
                  }`}>{s.l}</button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {d.status === "pursuing" ? (
                <div>
                  <label className={labelCls}>Current semester</label>
                  <input data-testid={`degree-sem-${i}`} value={d.current_semester} onChange={(e) => update(i, { current_semester: e.target.value })} placeholder="e.g. 4" className={`${inputCls} mt-1`} />
                </div>
              ) : (
                <div>
                  <label className={labelCls}>Completion year</label>
                  <input data-testid={`degree-year-${i}`} value={d.completion_year} onChange={(e) => update(i, { completion_year: e.target.value })} placeholder="e.g. 2024" className={`${inputCls} mt-1`} />
                </div>
              )}
              <div>
                <label className={labelCls}>Overall / current CGPA (optional)</label>
                <input data-testid={`degree-cgpa-${i}`} value={d.overall_cgpa} onChange={(e) => update(i, { overall_cgpa: e.target.value })} placeholder="e.g. 8.6" className={`${inputCls} mt-1`} />
              </div>
              {d.degree === "Other" && (
                <div>
                  <label className={labelCls}>Institution (optional)</label>
                  <input value={d.institution} onChange={(e) => update(i, { institution: e.target.value })} placeholder="e.g. NID" className={`${inputCls} mt-1`} />
                </div>
              )}
            </div>

            {/* semester grades */}
            <div className="mt-3">
              <label className={labelCls}>Semester-wise grades (optional)</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {d.semesters.map((s, j) => (
                  <div key={s.id || j} className="flex items-center gap-1">
                    <span className="text-[11px] text-slate-400 font-mono w-8">S{s.sem}</span>
                    <input data-testid={`degree-${i}-semgrade-${j}`} value={s.grade} onChange={(e) => setSem(i, j, e.target.value)} placeholder="8.2"
                      className="w-full px-2 py-1.5 rounded-md border border-slate-300 text-slate-900 text-xs focus:border-[#6366F1] outline-none" />
                    <button onClick={() => removeSem(i, j)} className="text-slate-300 hover:text-red-500"><X size={13} /></button>
                  </div>
                ))}
              </div>
              <button type="button" data-testid={`degree-addsem-${i}`} onClick={() => addSem(i)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#6366F1] hover:text-[#4F46E5] transition-colors">
                <Plus size={13} /> Add semester
              </button>
            </div>
          </div>
        ))}
      </div>

      <button type="button" data-testid="add-degree-btn" onClick={addDegree}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#6366F1] hover:text-[#4F46E5] transition-colors">
        <Plus size={15} /> Add {degrees.length ? "another " : ""}degree
      </button>
    </div>
  );
}

export { blank as blankDegree };
